import cn from 'classnames'
import {
  format,
  getDate,
  getMonth,
  getYear,
  isEqual,
  isWithinInterval
} from 'date-fns'

import { FilterType } from '.'

export const isMobile = (deviceWidth: number) => deviceWidth < 768

export const isInRange = (
  date: Date,
  startDate: Date | null | undefined,
  endDate: Date | null | undefined
) => {
  if (startDate && endDate) {
    return isWithinInterval(date, { start: startDate, end: endDate })
  }
  return false
}

export const getFormat = (
  dateFormat: string | string[] | undefined,
  showTimeSelect: boolean | undefined
) => {
  if (!dateFormat && showTimeSelect) {
    return `dd/MM/yyyy hh:mm aaa`
  } else if (!dateFormat && !showTimeSelect) {
    return 'dd/MM/yyyy'
  }
  return dateFormat
}

export const isSelectedDate = (
  date: Date,
  selected: Date | null | undefined
) => {
  if (selected) {
    return (
      getDate(selected) === getDate(date) &&
      getMonth(selected) === getMonth(date) &&
      getYear(selected) === getYear(date)
    )
  }
  return false
}

export const getTimeClassName = (
  date: Date,
  timeClassName?: (date: Date) => string | null
) =>
  cn(
    'text-xs leading-5 font-normal w-62px md:w-64px',
    `${timeClassName && timeClassName(date)}`
  )

export const getWeekdayClassName = (
  date: Date,
  showTimeSelect: boolean | undefined,
  weekDayClassName?: (date: Date) => string | null
) =>
  cn(
    'text-xs leading-4 font-normal text-blue-gray-700 m-0 p-8px w-39px h-32px mt-8px',
    `${showTimeSelect ? '' : 'mr-8px'}`,
    `${weekDayClassName && weekDayClassName(date)}`
  )

export const getDayClassName = (
  date: Date,
  selected: Date | null | undefined,
  selectsRange: boolean | undefined,
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
  showTimeSelect: boolean | undefined,
  dayClassName?: (date: Date) => string | null
) => {
  if (selectsRange) {
    if (startDate && isEqual(date, startDate)) {
      return cn(
        'text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px bg-blue-600 text-base-white m-0 px-10px md:px-12px py-8px hover:bg-blue-600',
        `${showTimeSelect ? '' : 'mr-8px'}`,
        `${dayClassName && dayClassName(date)}`
      )
    }
    if (endDate && isEqual(date, endDate)) {
      return cn(
        'text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px bg-blue-600 text-base-white m-0 px-10px md:px-12px py-8px hover:bg-blue-600',
        `${showTimeSelect ? '' : 'mr-8px'}`,
        `${dayClassName && dayClassName(date)}`
      )
    }
    if (isInRange(date, startDate, endDate)) {
      return cn(
        'text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px text-blue-gray-700 m-0 px-10px md:px-12px py-8px bg-blue-50 hover:bg-blue-50',
        `${showTimeSelect ? '' : 'mr-8px'}`,
        `${dayClassName && dayClassName(date)}`
      )
    }
  }
  return isSelectedDate(date, selected)
    ? cn(
        'text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px bg-blue-600 text-base-white m-0 px-10px md:px-12px py-8px hover:bg-blue-600 ',
        `${showTimeSelect ? '' : 'mr-8px'}`,
        `${dayClassName && dayClassName(date)}`
      )
    : cn(
        'text-center align-middle text-sm font-normal w-35px md:w-39px h-32px mt-8px md:mt-4px text-blue-gray-700 m-0 px-10px md:px-12px py-8px hover:bg-blue-50 ',
        `${showTimeSelect ? '' : 'mr-8px'}`,
        `${dayClassName && dayClassName(date)}`
      )
}

export const getPopperClassName = (popperClassName: string | undefined) =>
  cn(
    'bg-base-white rounded-8px border-solid border-1 border-blue-gray-200 pt-16px pl-16px pb-12px mt-8px',
    'popper',
    popperClassName
  )

export function getDefaultLocale() {
  const scope: any = typeof window !== 'undefined' ? window : global

  return scope.__localeId__
}

export function getLocaleObject(localeSpec: string | Locale | undefined) {
  if (typeof localeSpec === 'string') {
    // Treat it as a locale name registered by registerLocale
    const scope: any = typeof window !== 'undefined' ? window : global
    return scope.__localeData__ ? scope.__localeData__[localeSpec] : null
  } else {
    // Treat it as a raw date-fns locale object
    return localeSpec
  }
}

export function formatDate(
  date: Date | string,
  formatStr: string | undefined,
  locale: string | Locale | undefined
) {
  if (locale === 'en') {
    return format(date as any, formatStr as any)
  }
  let localeObj = getLocaleObject(locale)
  if (locale && !localeObj) {
    console.warn(
      `A locale object was not found for the provided string ["${locale}"].`
    )
  }
  if (
    !localeObj &&
    !!getDefaultLocale() &&
    !!getLocaleObject(getDefaultLocale())
  ) {
    localeObj = getLocaleObject(getDefaultLocale())
  }
  return format(date as any, formatStr as any, {
    locale: localeObj ? localeObj : null
  })
}

export function safeDateFormat(
  date: Date | string,
  {
    dateFormat,
    locale
  }: {
    dateFormat: string | string[] | undefined
    locale: string | Locale | undefined
  }
) {
  return (
    (date &&
      formatDate(
        date,
        Array.isArray(dateFormat) ? dateFormat[0] : dateFormat,
        locale
      )) ||
    ''
  )
}

export function safeDateRangeFormat(
  startDate: Date | string,
  endDate: Date | string,
  props: {
    dateFormat: string | string[] | undefined
    locale: string | Locale | undefined
  }
) {
  if (!startDate) {
    return ''
  }

  const formattedStartDate = safeDateFormat(startDate, props)
  const formattedEndDate = endDate ? safeDateFormat(endDate, props) : ''

  return `${formattedStartDate} - ${formattedEndDate}`
}

export const getSelectedFilterLabel = (
  filters: Array<FilterType> | undefined,
  selectedFilterValue: string | undefined
) =>
  filters?.find((eachFilter) => eachFilter.value === selectedFilterValue)
    ?.label ?? ''
